/* eslint-disable no-param-reassign */
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid'
import { get } from 'lodash'

// eslint-disable-next-line import/no-cycle
import { INVOICE_CONFIGURATOR_DATA_GRID } from 'containers/billing/settings'
import { INVOICE_PAGINATION } from 'settings/constants/pagination'
// eslint-disable-next-line import/no-cycle
import { isPaidInvoice, isUnPaidInvoice } from 'utils/billing'
import { filterMapToQueryParams } from 'utils/LHSbracket'
import { handleCommonTableformatFilters, handleCommonTableformatSortBy, handlePrefillResponseModification } from 'utils/table'

import { createDataGridSlice, mapSortRulesToSortModel } from '../generic/createDataGridSlice'
import { fixDateFilters } from '../generic/fixDateFilters'

const { INITIAL_PAGE, ROWS_PER_PAGE } = INVOICE_PAGINATION

const calculateEmailOnlyAction = selectedRowsStatusCount => {
  const { paid, unpaid, partial } = selectedRowsStatusCount
  const totalSelected = paid + unpaid + partial

  const isUnPaid = unpaid === totalSelected && totalSelected > 0
  const isPaid = paid === totalSelected && totalSelected > 0
  const isMixUnpaidAndPaid = unpaid > 0 && paid > 0 && partial === 0

  return {
    show: isMixUnpaidAndPaid || isUnPaid || isPaid,
    disabled: false,
  }
}

const getInitialState = () => ({
  pagination: {
    page: INITIAL_PAGE,
    pageSize: ROWS_PER_PAGE,
  },
  sorting: {
    sortModel: [{ field: 'invoiceDate', sort: 'desc' }],
  },
  columns: {
    columnVisibilityModel: {
      customerBillingProfile: true,
      accountNumber: true,
      customerName: true,
      invoiceNumber: true,
      invoiceDate: true,
      sentDate: true,
      postDate: false,
      postedAmountCents: false,
      amount: true,
      balance: true,
      status: true,
      generationStatus: true,
      wo: false,
      service: false,
      unPostedAmountCents: false,
      lateFees: false,
      paymentNumber: false,
      billingCycleText: false,
      billingTiming: false,
      type: false,
      paymentTermsText: false,
      paper: false,
      pdf: false,
      portal: false,
      receivesPrintedCopy: false,
      deliveryMethod: false,
      customerAddress: false,
      customerCity: false,
      customerState: false,
      customerZip: false,
      tags: true,
      adjustments: false,
      adjustmentsTotal: false,
      legacyPostDate: false,
      legacyPostedAmountCents: false,
    },
  },
  confirmInvoiceBulkActionDialog: {
    open: false,
    action: '',
    invoiceCount: 0,
  },
  selectionModel: [],
  selectedRows: [],
  selectedInvoiceIdList: [],
  selectedRowsStatusCount: {
    paid: 0,
    unpaid: 0,
    partial: 0,
    unpaidWithZeroAmount: 0,
    paidWithZeroAmount: 0,
    unpaidCreditMemo: 0,
    paidCreditMemo: 0,
  },
  isOpenInvoiceActionMenu: null,
  isListLoading: false,
  savedView: {
    existingFilterList: [],
    activeFilter: '',
    input: '',
    selectedFilterId: '',
    selectedFilter: {},
    isModalOpen: false,
  },
  filters: {
    allFilters: [],
    isOpenTableFilters: false,
    searchFilters: {},
    sortRules: [{ columnName: 'invoiceDate', operation: 'DESC' }],
    refetchFlag: false,
    queryParams: {},
  },
  sendPostData: {
    draftInvoicesWithoutPDF: [],
    isOpenInvoiceSendFailureConfirmation: false,
  },
  deleteInvoiceConfirmation: {
    isOpen: false,
    invoiceIds: [],
    invoiceNumbers: [],
  },
  deleteUnpaidInvoiceConfirmation: {
    isOpen: false,
    invoiceId: '',
  },
  regenerateConfirmation: {
    isOpen: false,
    invoiceIds: [],
    invoiceNumbers: [],
    invoices: [],
    isMultiSelect: false,
  },
  revertToDraftConfirmation: {
    isOpen: false,
    invoiceId: '',
  },
  revertCreditMemoToDraftConfirmation: {
    isOpen: false,
    invoiceId: '',
  },
  moveToAccountCreditConfirmation: {
    isOpen: false,
    accountId: '',
    invoiceNumbers: [],
    paymentNumbers: [],
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: ['actions'],
  },
  sideView: {
    isOpen: false,
    isEditInvoiceWOListingOpen: false,
    currentInvoice: {
      accountId: '',
      invoiceNumber: '',
      invoiceId: '',
      unPostedAmountCents: '',
      accountName: '',
      invoiceDate: '',
      invoiceStatus: '',
      isCurrentUnPosted: false,
    },
  },
  markAsPaidAction: {
    show: false,
    disabled: true,
  },
  markAsSentAction: {
    show: false,
    disabled: true,
  },
  regenerateAction: {
    show: false,
    disabled: true,
  },
  deleteAction: {
    show: false,
    disabled: true,
  },
  postAction: {
    show: false,
    disabled: true,
  },
  emailOnlyAction: {
    show: false,
    disabled: true,
  },
  postDraftsAction: {
    show: false,
    disabled: true,
  },
  postAndSendAction: {
    show: false,
    disabled: true,
  },
  postDraftsAndSendAction: {
    show: false,
    disabled: true,
  },

  resetOnUnmount: true,
  payments: {
    selectedRow: undefined,
    isOpenTakePaymentsDialog: false,
  },
  bulkSendInvoicesConfirmation: {
    isOpen: false,
  },
  addToGroup: {
    isOpen: false,
    invoiceIds: [],
  },
  revertToDraftAction: {
    show: false,
    disabled: false,
  },
  bulkRevertBackToDraftConfirmation: {
    isOpen: false,
  },
  customEmailDialog: {
    isOpen: false,
    invoice: null,
  },
  // Cross-component signal: bumped to ask a customer's invoice list to (re)start generation polling
  // after an external regenerate (e.g. the payment-terms / billing-profile "regenerate drafts" prompt),
  // mirroring the manual regenerate's activatePolling so the per-invoice progress bar shows every time.
  draftRegenerationPolling: {
    accountId: null,
    nonce: 0,
  },
})
export const invoicesSlice = createDataGridSlice({
  name: 'invoices',
  initialState: getInitialState(),
  reducers: {
    setInvoicesSelectionModel: (state, action) => {
      const selectionModel = get(action, 'payload.selectionModel', [])
      const rows = get(action, 'payload.rows', [])
      state.selectionModel = selectionModel
      if (selectionModel.length > 0) {
        const selectedInvoiceNumbersSet = new Set(selectionModel)
        state.selectedRows = rows.filter(({ invoiceNumber }) => selectedInvoiceNumbersSet.has(invoiceNumber))
        state.selectedInvoiceIdList = state.selectedRows.map(({ invoiceId }) => invoiceId)
        const newSelectedRowsStatusCount = { ...getInitialState().selectedRowsStatusCount }
        state.selectedRows.forEach(({ status, amountCents }) => {
          const statusLower = status.toLowerCase()
          newSelectedRowsStatusCount[statusLower] += 1
          if (isUnPaidInvoice(status) && amountCents === 0) {
            newSelectedRowsStatusCount.unpaidWithZeroAmount += 1
          }
          if (isPaidInvoice(status) && amountCents === 0) {
            newSelectedRowsStatusCount.paidWithZeroAmount += 1
          }
          if (isUnPaidInvoice(status) && amountCents < 0) {
            newSelectedRowsStatusCount.unpaidCreditMemo += 1
          }
          if (isPaidInvoice(status) && amountCents < 0) {
            newSelectedRowsStatusCount.paidCreditMemo += 1
          }
        })
        const { paid, unpaid, partial, unpaidWithZeroAmount, paidWithZeroAmount, paidCreditMemo } = newSelectedRowsStatusCount
        const totalSelected = paid + unpaid + partial
        state.selectedRowsStatusCount = newSelectedRowsStatusCount

        // Existing actions
        state.regenerateAction = { show: partial > 0 || unpaid > 0, disabled: paid > 0 }
        state.revertToDraftAction = {
          show: paidWithZeroAmount > 0 || paidCreditMemo > 0 || unpaid > 0,
          disabled: false,
        }
        state.markAsPaidAction = { show: unpaidWithZeroAmount > 0, disabled: partial > 0 || paid > 0 || unpaid - unpaidWithZeroAmount > 0 }
        state.markAsSentAction = { show: partial > 0, disabled: false }
        state.deleteAction = { show: partial > 0, disabled: paid > 0 || unpaid > 0 }

        // New actions based on selection state
        // Only Draft state (partial === totalSelected)
        const isOnlyDraft = partial === totalSelected && totalSelected > 0
        // Mix of unpaid and draft (partial > 0 && unpaid > 0 && paid === 0)
        const isMixUnpaidAndDraft = partial > 0 && unpaid > 0 && paid === 0

        // Post action (rename of "Mark as Sent" for Only Draft)
        state.postAction = { show: isOnlyDraft, disabled: false }

        // Email only (Only Draft, Mix, Paid)
        state.emailOnlyAction = calculateEmailOnlyAction(newSelectedRowsStatusCount)

        // Post drafts (Mix of unpaid and draft)
        state.postDraftsAction = { show: isMixUnpaidAndDraft, disabled: false }

        // Post & Send (Only Draft buttons)
        state.postAndSendAction = { show: isOnlyDraft, disabled: false }

        // Post drafts & send (Mix unpaid and draft buttons)
        state.postDraftsAndSendAction = { show: isMixUnpaidAndDraft, disabled: false }
      } else {
        const initialState = getInitialState()
        state.selectedRows = []
        state.selectedRowsStatusCount = initialState.selectedRowsStatusCount
        state.regenerateAction = initialState.regenerateAction
        state.revertToDraftAction = initialState.revertToDraftAction
        state.markAsPaidAction = initialState.markAsPaidAction
        state.markAsSentAction = initialState.markAsSentAction
        state.deleteAction = initialState.deleteAction
        state.postAction = initialState.postAction
        state.emailOnlyAction = initialState.emailOnlyAction
        state.postDraftsAction = initialState.postDraftsAction
        state.postAndSendAction = initialState.postAndSendAction
        state.postDraftsAndSendAction = initialState.postDraftsAndSendAction
      }
    },
    filterInvoicesSelectionModel: (state, action) => {
      const selectionModel = get(action, 'payload.selectionModel', [])
      state.selectionModel = selectionModel
      if (selectionModel.length > 0) {
        const selectedInvoiceNumbersSet = new Set(selectionModel)
        state.selectedRows = state.selectedRows.filter(({ invoiceNumber }) => selectedInvoiceNumbersSet.has(invoiceNumber))
        state.selectedInvoiceIdList = state.selectedRows.map(({ invoiceId }) => invoiceId)
        const newSelectedRowsStatusCount = { ...getInitialState().selectedRowsStatusCount }
        state.selectedRows.forEach(({ status, amountCents }) => {
          const statusLower = status.toLowerCase()
          newSelectedRowsStatusCount[statusLower] += 1
          if (isUnPaidInvoice(status) && amountCents === 0) {
            newSelectedRowsStatusCount.unpaidWithZeroAmount += 1
          }
          if (isPaidInvoice(status) && amountCents === 0) {
            newSelectedRowsStatusCount.paidWithZeroAmount += 1
          }
          if (isUnPaidInvoice(status) && amountCents < 0) {
            newSelectedRowsStatusCount.unpaidCreditMemo += 1
          }
          if (isPaidInvoice(status) && amountCents < 0) {
            newSelectedRowsStatusCount.paidCreditMemo += 1
          }
        })
        const { paid, unpaid, partial, unpaidWithZeroAmount } = newSelectedRowsStatusCount
        const totalSelected = paid + unpaid + partial
        state.selectedRowsStatusCount = newSelectedRowsStatusCount

        // Existing actions
        state.regenerateAction = { show: partial > 0 || unpaid > 0, disabled: paid > 0 }
        state.revertToDraftAction = { show: unpaid > 0, disabled: false }
        state.markAsPaidAction = { show: unpaidWithZeroAmount > 0, disabled: partial > 0 || paid > 0 || unpaid - unpaidWithZeroAmount > 0 }
        state.markAsSentAction = { show: partial > 0, disabled: false }
        state.deleteAction = { show: partial > 0, disabled: paid > 0 || unpaid > 0 }

        // New actions based on selection state
        const isOnlyDraft = partial === totalSelected && totalSelected > 0
        const isMixUnpaidAndDraft = partial > 0 && unpaid > 0 && paid === 0

        state.postAction = { show: isOnlyDraft, disabled: false }
        state.emailOnlyAction = calculateEmailOnlyAction(newSelectedRowsStatusCount)
        state.postDraftsAction = { show: isMixUnpaidAndDraft, disabled: false }
        state.postAndSendAction = { show: isOnlyDraft, disabled: false }
        state.postDraftsAndSendAction = { show: isMixUnpaidAndDraft, disabled: false }
      } else {
        const initialState = getInitialState()
        state.selectedRows = []
        state.selectedRowsStatusCount = initialState.selectedRowsStatusCount
        state.regenerateAction = initialState.regenerateAction
        state.revertToDraftAction = initialState.revertToDraftAction
        state.markAsPaidAction = initialState.markAsPaidAction
        state.markAsSentAction = initialState.markAsSentAction
        state.deleteAction = initialState.deleteAction
        state.postAction = initialState.postAction
        state.emailOnlyAction = initialState.emailOnlyAction
        state.postDraftsAction = initialState.postDraftsAction
        state.postAndSendAction = initialState.postAndSendAction
        state.postDraftsAndSendAction = initialState.postDraftsAndSendAction
      }
    },
    setInvoicesSavedViewSelectedFilterId: (state, action) => {
      state.savedView.selectedFilterId = action.payload
    },
    setInvoicesExistingFilterList: (state, action) => {
      state.savedView.existingFilterList = action.payload
    },
    setInvoicesSavedViewInput: (state, action) => {
      state.savedView.input = action.payload
    },
    setInvoicesSavedViewState: (state, action) => {
      state.savedView = {
        ...state.savedView,
        ...action.payload,
      }
    },
    setInvoicesRefetchFlag: (state, action) => {
      state.filters.refetchFlag = action.payload
    },
    setInvoicesFiltersAndSortRulesFromSavedView: (state, action) => {
      const newAllFilters = {}
      const newSortBy = {}
      const searchFilters = get(action, 'payload.searchFilters', [])
      const sortRules = get(action, 'payload.sortRules', [])
      if (Array.isArray(searchFilters) && searchFilters.length > 0) {
        searchFilters.forEach(filter => {
          const initialFilter = INVOICE_CONFIGURATOR_DATA_GRID.find(({ columnName }) => columnName === filter.columnName)
          newAllFilters[filter.columnName] = handlePrefillResponseModification(initialFilter, filter)
        })
        state.filters.allFilters = newAllFilters
        state.filters.queryParams = filterMapToQueryParams(newAllFilters)
        const parsedFilters = handleCommonTableformatFilters(newAllFilters)
        state.filters.searchFilters = fixDateFilters(parsedFilters)
      } else {
        state.filters.queryParams = {}
        state.filters.allFilters = {}
        state.filters.searchFilters = {}
      }
      if (Array.isArray(sortRules) && sortRules.length > 0) {
        sortRules.forEach(sort => {
          newSortBy[sort.columnName] = { columnName: sort.columnName, operation: sort.operation }
        })
        const newSortRules = handleCommonTableformatSortBy(newSortBy)
        state.filters.sortRules = newSortRules
        state.sorting.sortModel = mapSortRulesToSortModel(newSortRules)
      } else {
        state.filters.sortRules = {}
        state.sorting.sortModel = []
      }
      state.filters.refetchFlag = true
    },
    setInvoicesSideView: (state, action) => {
      state.sideView = {
        ...state.sideView,
        ...action.payload,
      }
    },
    setInvoicesSelectedInvoice: (state, action) => {
      state.sideView.currentInvoice = {
        ...state.sideView.currentInvoice,
        ...action.payload,
      }
    },
    setInvoicesSendPostData: (state, action) => {
      state.sendPostData = {
        ...state.sendPostData,
        ...action.payload,
      }
    },
    setInvoicesDeleteConfirmation: (state, action) => {
      state.deleteInvoiceConfirmation = {
        ...state.deleteInvoiceConfirmation,
        ...action.payload,
      }
    },
    setInvoicesRegenerateConfirmation: (state, action) => {
      state.regenerateConfirmation = {
        ...state.regenerateConfirmation,
        ...action.payload,
      }
    },
    setInvoicesRevertToDraftConfirmation: (state, action) => {
      state.revertToDraftConfirmation = {
        ...state.revertToDraftConfirmation,
        ...action.payload,
      }
    },
    setInvoicesRevertCreditMemoToDraftConfirmation: (state, action) => {
      state.revertCreditMemoToDraftConfirmation = {
        ...state.revertCreditMemoToDraftConfirmation,
        ...action.payload,
      }
    },
    setInvoicesMoveToAccountCreditConfirmation: (state, action) => {
      state.moveToAccountCreditConfirmation = {
        ...state.moveToAccountCreditConfirmation,
        ...action.payload,
      }
    },
    setInvoicesDeleteUnpaidConfirmation: (state, action) => {
      state.deleteUnpaidInvoiceConfirmation = {
        ...state.deleteUnpaidInvoiceConfirmation,
        ...action.payload,
      }
    },
    setInvoicesIsOpenInvoiceActionMenu: (state, action) => {
      state.isOpenInvoiceActionMenu = action.payload
    },
    resetInvoicesState: state => {
      if (state.resetOnUnmount) return getInitialState()
      return state
    },
    setResetInvoicesStateOnUnmount: (state, action) => {
      state.resetOnUnmount = action.payload
    },
    setInvoicesPaymentsState: (state, action) => {
      state.payments = action.payload
    },
    setBulkSentInvoicesConfirmation: (state, action) => {
      state.bulkSendInvoicesConfirmation = {
        ...state?.bulkSendInvoicesConfirmation,
        ...action.payload,
      }
    },
    setBulkRevertBackToDraftConfirmation: (state, action) => {
      state.bulkRevertBackToDraftConfirmation = {
        ...state.bulkRevertBackToDraftConfirmation,
        ...action.payload,
      }
    },
    setIsInvoiceListLoading: (state, action) => {
      state.isListLoading = action.payload
    },
    setAddToGroupDialogState: (state, action) => {
      state.addToGroup = action.payload
    },
    setConfirmInvoiceBulkActionDialog: (state, action) => {
      state.confirmInvoiceBulkActionDialog = {
        ...state.confirmInvoiceBulkActionDialog,
        ...action.payload,
      }
    },
    setCustomEmailDialogState: (state, action) => {
      state.customEmailDialog = {
        ...state.customEmailDialog,
        ...action.payload,
      }
    },
    triggerDraftRegenerationPolling: (state, action) => {
      state.draftRegenerationPolling = {
        accountId: action.payload,
        nonce: (state.draftRegenerationPolling?.nonce ?? 0) + 1,
      }
    },
  },
})

export const {
  setInvoicesExistingFilterList,
  setInvoicesSelectionModel,
  filterInvoicesSelectionModel,
  setSortModel: setInvoicesSortModel,
  setPage: setInvoicesPage,
  setPageSize: setInvoicesPageSize,
  setColumnVisibilityModel: setInvoicesColumnVisibilityModel,
  setFilters: setInvoicesFilters,
  setIsOpenTableFilters: setInvoicesIsOpenTableFilters,
  setPinnedColumns: setInvoicesPinnedColumns,
  setInvoicesSavedViewInput,
  setInvoicesSavedViewState,
  setInvoicesFiltersAndSortRulesFromSavedView,
  setInvoicesRefetchFlag,
  setInvoicesSavedViewSelectedFilterId,
  setInvoicesSideView,
  setInvoicesSelectedInvoice,
  setInvoicesSendPostData,
  setInvoicesDeleteConfirmation,
  setInvoicesIsOpenInvoiceActionMenu,
  setInvoicesRegenerateConfirmation,
  setResetInvoicesStateOnUnmount,
  resetInvoicesState,
  setInvoicesPaymentsState,
  setInvoicesRevertToDraftConfirmation,
  setInvoicesRevertCreditMemoToDraftConfirmation,
  setInvoicesMoveToAccountCreditConfirmation,
  setInvoicesDeleteUnpaidConfirmation,
  setBulkSentInvoicesConfirmation,
  setIsInvoiceListLoading,
  setAddToGroupDialogState,
  setConfirmInvoiceBulkActionDialog,
  setBulkRevertBackToDraftConfirmation,
  setCustomEmailDialogState,
  triggerDraftRegenerationPolling,
} = invoicesSlice.actions

export const selectIsOpenInvoiceSendFailureConfirmation = s => get(s, 'invoices.sendPostData.isOpenInvoiceSendFailureConfirmation', false)
export const selectDraftInvoicesWithoutPDF = s => get(s, 'invoices.sendPostData.draftInvoicesWithoutPDF', [])
export const selectConfirmInvoiceBulkActionDialog = s => get(s, 'invoices.confirmInvoiceBulkActionDialog', {})
export const selectIsOpenBulkRevertBackToDraftConfirmation = s => get(s, 'invoices.bulkRevertBackToDraftConfirmation.isOpen', false)
export const selectMoveToAccountCreditConfirmation = s => get(s, 'invoices.moveToAccountCreditConfirmation', {})
export const selectCustomEmailDialog = s => get(s, 'invoices.customEmailDialog', { isOpen: false, invoice: null })

export default invoicesSlice.reducer
